import React from 'react';
import PropTypes from 'prop-types';
import { Portal } from 'react-portal';

import Tabs from 'relements/build/esm/ui/tabs';
import Loader from 'relements/build/esm/ui/loader';

import {
  INIT_CODE_CODE_NODE,
  INIT_CODE_STATIC_NODE,
  INIT_CODE_VOICE_AGENT,
} from '@src/containers/CodeNode/utils/const';
import { sendGoogleAnalyticsEvents } from '@src/actions/_utils';
import { GOOGLE_ANALYTICS } from '@src/const';

import { createAlert } from 'actions/BotActions';
import { AlertTypes } from 'constants/MogamboConstants';

import EditorHeader from './components/EditorHeader';
import ResizablePanels from './components/ResizablePanels';
import Variables from './components/Variables';

import styles from './CodeEditor.scss';

let AceEditor = () => null;

const EDITOR_CONSOLE = [
  {
    title: 'Run Output',
    path: 'output',
  },
  {
    title: 'Sample input data (JSON)',
    path: 'input',
  },
];

class CodeEditor extends React.Component {
  constructor(props) {
    super(props);

    this.panels = React.createRef();

    this.state = {
      loading: false,
      importing: true,
      canRun: false,
      edited: false,
      tab: 'input',
      inputJSON: '',
      code: '',
      runOutput: '',
      envDrawerActive: false,
      envVariablesEdited: false,
      envVariables: [],
    };
  }

  componentDidMount() {
    import('react-ace').then((ace) => {
      AceEditor = ace.default;
      import('brace/mode/python')
        .then(import('brace/mode/json'))
        .then(import('brace/mode/powershell'))
        .then(import('brace/theme/tomorrow'))
        .finally(() => {
          if (this.props.noCustomLogic) {
            this.setState({
              importing: false,
              code: this.props.code,
            });
            return;
          }

          if (this.props.code && this.props.inputJSON) {
            if (this.props.code.trim()) {
              this.setState({
                importing: false,
                canRun: true,
                inputJSON: this.props.inputJSON,
                code: this.props.code,
                envVariables: [...this.props.envVariables],
              });
            } else {
              this.setState({
                importing: false,
                canRun: false,
                inputJSON: this.props.inputJSON,
                code: this.props.code,
                envVariables: [...this.props.envVariables],
              });
            }
          } else {
            const codeNodeCode = this.props.isVoiceAgent
              ? INIT_CODE_VOICE_AGENT
              : INIT_CODE_CODE_NODE;
            this.setState({
              importing: false,
              edited: true,
              inputJSON: this.props.inputJSON,
              // TODO: REMOVE THIS CHECK WHEN REMOVING CODE EDITOR OUT OF STATIC NODE
              code:
                this.props.code || this.props.isCodeNode
                  ? codeNodeCode
                  : INIT_CODE_STATIC_NODE,
              envVariables: [...this.props.envVariables],
            });
          }
        });
    });
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (
      nextProps.envVariables !== prevState.envVariables &&
      !prevState.envVariablesEdited
    ) {
      return { envVariables: nextProps.envVariables };
    }

    // Return null if the state hasn't changed
    return null;
  }

  render() {
    let loader;
    if (this.state.loading || this.state.importing) {
      loader = (
        <div className={`${styles.editorLoader}`}>
          <Loader />
        </div>
      );
    }

    const commonProps = {
      theme: 'tomorrow',
      fontSize: 14,
      showPrintMargin: false,
      wrapEnabled: true,
      width: '100%',
    };

    return (
      <Portal node={document && document.getElementById('app-body')}>
        <div className={styles.editorWrapper} id="editor-wrapper">
          <EditorHeader
            lastSaved={this.props.lastEditedTime}
            onClose={this._navToMenu}
            edited={this.state.edited}
            onSave={this._save}
            onRun={this._run}
            canRun={this.state.canRun}
            onContextClick={this._onContextClick}
            envVariablesEnabled={this.props.envVariablesEnabled}
            noRun={this.props.noRun}
          />
          {!this.state.importing ? (
            <ResizablePanels ref={this.panels}>
              {(height) => (
                <AceEditor
                  {...commonProps}
                  mode="python"
                  height={height}
                  value={this.state.code}
                  onChange={(value) => this._handleCodeChange(value)}
                  name="editor-main"
                  focus
                  className={`${styles.aceEditor}`}
                  editorProps={{ $blockScrolling: true }}
                  style={{ transition: 'inherit' }}
                />
              )}
              {(height) => (
                <div style={{ height, transition: 'inherit' }}>
                  <Tabs
                    className={styles.editorConsoleTab}
                    value={this.state.tab}
                  >
                    {EDITOR_CONSOLE.map((tab) => (
                      <Tabs.Item
                        onClick={(currentTab) =>
                          this.setState({ tab: currentTab })
                        }
                        key={tab.path}
                        value={tab.path}
                      >
                        {tab.title}
                      </Tabs.Item>
                    ))}
                  </Tabs>
                  {this.renderTab(commonProps, height)}
                </div>
              )}
            </ResizablePanels>
          ) : (
            <div style={{ height: '100%', width: '100%' }} />
          )}
          <Variables
            active={this.state.envDrawerActive}
            onClose={() => this.setState({ envDrawerActive: false })}
            envVariables={this.state.envVariables}
            hasChanged={(variables) => this._setEdited(variables)}
          />
          {loader}
        </div>
      </Portal>
    );
  }

  renderTab = (commonProps, height) => {
    // 31 is the height of Tabs component in the div
    const consoleHeight = height - this.props.headerHeight;

    if (this.state.tab === 'output') {
      return (
        <AceEditor
          {...commonProps}
          mode="powershell"
          height={`${consoleHeight}px`}
          value={this.state.runOutput}
          onChange={() => {}}
          name="editor-console"
          className={`${styles.aceEditor}`}
          editorProps={{ $blockScrolling: true }}
          style={{ zIndex: '9999' }}
          readOnly
        />
      );
    }
    if (this.state.tab === 'input') {
      return (
        <AceEditor
          {...commonProps}
          mode="json"
          height={`${consoleHeight}px`}
          value={this.state.inputJSON}
          onChange={(val) => this.setState({ inputJSON: val })}
          name="editor-json"
          className={`${styles.aceEditor}`}
          editorProps={{ $blockScrolling: true }}
          style={{ zIndex: '9999' }}
        />
      );
    }

    return null;
  };

  _onContextClick = () => {
    if (!this.state.envDrawerActive) {
      this.setState({ envDrawerActive: true });
    }
  };

  _navToMenu = () => {
    this.props.navToMenu(this.state.edited);
  };

  _save = () => {
    if (!this.state.edited || !this.state.code.trim()) {
      createAlert('Error! Code cannot be empty.', null, AlertTypes.NEGATIVE);
      return;
    }

    if (this.state.envVariablesEdited) {
      if (!this._validateKeys()) {
        createAlert(
          'Error! Key, staging or production values cannot be empty',
          null,
          AlertTypes.NEGATIVE
        );
        return;
      }

      this.setState({ loading: true, runOutput: '', tab: 'input' });

      this.props.onSaveWithVariables(
        this.state.envVariables,
        this.state.code,
        (success) => {
          if (success) {
            this.setState({
              loading: false,
              edited: false,
              envVariablesEdited: false,
              canRun: true,
            });
          } else {
            this.setState({ loading: false, edited: true, canRun: false });
          }
        }
      );
    } else {
      this.setState({ loading: true, runOutput: '', tab: 'input' });

      this.props.onSave(this.state.code, (success) => {
        if (success) {
          this.setState({ loading: false, edited: false, canRun: true });
        } else {
          this.setState({ loading: false, edited: true, canRun: false });
        }
      });
    }

    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.CODE_EDITOR,
      GOOGLE_ANALYTICS.EVENTS.CODE_EDITOR_SAVE,
      this.props.id,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  };

  _run = () => {
    if (this.state.edited || !this.state.code) return;

    this.setState({
      loading: true,
      tab: 'output',
      runOutput: '',
      canRun: false,
      envDrawerActive: false,
    });

    this.props.onRun(this.state.inputJSON, (e) => {
      if (this.panels.current) {
        this.panels.current.animateConsole();
      }

      this.setState({
        loading: false,
        runOutput: e,
        canRun: true,
      });
    });

    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.CODE_EDITOR,
      GOOGLE_ANALYTICS.EVENTS.CODE_EDITOR_RUN,
      this.props.id,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  };

  _handleCodeChange = (value) => {
    if (!value.trim()) {
      this.setState({ code: value, edited: false, canRun: false });
      return;
    }

    if (value !== this.props.code) {
      this.setState({ code: value, edited: true, canRun: false });
    } else {
      this.setState({ code: value, edited: false, canRun: true });
    }
  };

  _setEdited = (variables) => {
    this.setState({
      edited: true,
      canRun: false,
      envVariablesEdited: true,
      envVariables: variables,
    });
  };

  _validateKeys = () => {
    let validKeys = true;

    this.state.envVariables.every((variable) => {
      if (!variable.key || !variable.staging || !variable.production) {
        validKeys = false;
      }
    });

    return validKeys;
  };
}

CodeEditor.propTypes = {
  id: PropTypes.string,
  headerHeight: PropTypes.number,
  lastEditedTime: PropTypes.string,
  code: PropTypes.string,
  envVariables: PropTypes.shape([]),
  edited: PropTypes.bool,
  // TODO: REMOVE THIS CHECK WHEN REMOVING CODE EDITOR OUT OF STATIC NODE
  isCodeNode: PropTypes.bool,
  noRun: PropTypes.bool,
  noCustomLogic: PropTypes.bool,
  envVariablesEnabled: PropTypes.bool,
  inputJSON: PropTypes.string,
  handleCodeChange: PropTypes.func,
  navToMenu: PropTypes.func,
  onSave: PropTypes.func,
  onRun: PropTypes.func,
  onSaveWithVariables: PropTypes.func,
};

CodeEditor.defaultProps = {
  id: '',
  headerHeight: 0,
  lastEditedTime: '',
  code: '',
  edited: false,
  // TODO: REMOVE THIS CHECK WHEN REMOVING CODE EDITOR OUT OF STATIC NODE
  isCodeNode: false,
  noRun: false,
  noCustomLogic: false,
  envVariablesEnabled: false,
  inputJSON: '',
  envVariables: [],
  handleCodeChange: () => {},
  navToMenu: () => {},
  onSave: () => {},
  onRun: () => {},
  onSaveWithVariables: () => {},
};

export default CodeEditor;
